/* screens/checkout/BuyPlan.jsx — публичная ссылка покупки личного тарифа: /buy/:slug.

   Ссылку выкладывают в соцсети (первая — акция «EXPERT 1=2», октябрь 2026). Путь
   нарочно короткий: требование маркетинга — «перешёл и сразу на оплату», без
   «зарегистрируйся, потом найди тариф в настройках».
   - Вошёл → карточка тарифа и одна кнопка оплаты (телефон — если его ещё нет).
     Если идёт оплаченный срок любого платного тарифа (и Start — решение владельца
     19.10.2026), страница говорит, с какой даты акция начнётся (сервер — `quote`,
     до него — PlanCard.queuedStartsAt, зеркало бэка).
   - Не вошёл → короткая регистрация здесь же. Подтверждение почты остаётся ДО
     оплаты (решение владельца 15.10.2026), но адрес этой страницы едет в письмо:
     клик по ссылке подтверждения сразу входит и возвращает сюда (VerifyEmail).
   - Есть аккаунт → «Войти» с возвратом сюда же (SignIn понимает ?next=).
   Покупка идёт в ЛИЧНОЕ пространство: сервер отдаёт по этой ссылке только
   публичные личные тарифы (`PublicOfferView`). */

import React from 'react';
import { useAuth } from '../../app/providers/AuthProvider.jsx';
import { currentPriceOf, fmtSeconds, queuedStartsAt } from '../../components/patterns/PlanCard.jsx';
import { usePhoneGate } from '../../components/patterns/PhoneGate.jsx';
import { RecurringConsentModal } from '../../components/patterns/RecurringConsentModal.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Field, PasswordInput, TextInput } from '../../components/ui/Field.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { subscriptionReal } from '../../lib/adapters/subscription.js';
import { authReal } from '../../lib/api/index.js';
import { getOffer } from '../../lib/api/subscription.js';
import { fmt } from '../../lib/format.js';
import { consentNeededForPlan, isConsentRequiredError } from '../../lib/recurringConsent.js';
import { RR } from '../../lib/router.jsx';
import { CheckoutLayout } from './CheckoutLayout.jsx';
import { CheckoutRow } from './CheckoutRow.jsx';

  const { useState, useEffect, useRef, useCallback } = React;
  const { useParams, useNavigate, Link } = RR;

  const EMAIL_RE = /^[^\s@.]+(?:\.[^\s@.]+)*@[^\s@.]+(?:\.[^\s@.]+)+$/;

  function periodText(days) {
    if (!days) return '—';
    if (days % 30 === 0) {
      const m = days / 30;
      return m === 1 ? '1 месяц' : m < 5 ? m + ' месяца' : m + ' месяцев';
    }
    return days + ' дн.';
  }

  // Последний день продаж: граница окна на сервере не включительна (00:00 следующего дня).
  function lastSaleDay(saleEndsAt) {
    return saleEndsAt ? new Date(new Date(saleEndsAt).getTime() - 1) : null;
  }

  /* Плашка «акция начнётся после текущего». Дата старта приходит из `quote`
     оффера, а подписка — ОТДЕЛЬНЫМ запросом и обычно позже, поэтому `sub` здесь
     законно бывает null: обращение `sub.plan_name` роняло всю страницу в белый
     экран у каждого держателя Expert (QA 16.10.2026). Без подписки — нейтральное
     «текущий тариф», с ней — его название. */
  function QueuedCallout({ sub, offer, startsAt }) {
    const current = sub && sub.plan_name ? '«' + sub.plan_name + '»' : 'текущий тариф';
    return (
      <div className="callout callout--info" role="status">
        <Icon name="info" size={16} />
        <span>
          Сейчас у вас действует {current} до {fmt.date(startsAt)}.
          Оплатите сейчас — «{offer.name}» начнётся {fmt.date(startsAt)}, когда он закончится.
        </span>
      </div>
    );
  }

  /* Подпись «С зачётом остатка…» — только когда сервер посчитал доплату апгрейда
     (`upgrade_proration`) и она МЕНЬШЕ цены тарифа. Акция с 19.10.2026 — всегда
     `initial` по полной цене (у держателя Start — в очередь за ним), а «доплата» на всю
     цену (срок текущего уже вышел) ничего не зачитывает: обещать зачёт там — неправда. */
  function isProratedQuote(quote, offer) {
    if (!quote || !offer || quote.kind !== 'upgrade_proration') return false;
    const amount = Number(quote.amount_rub);
    const price = Number(offer.price_rub);
    return Number.isFinite(amount) && Number.isFinite(price) && amount < price;
  }

  function BuyPlan() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, workspaces } = useAuth();
    const phoneGate = usePhoneGate();
    const selfPath = '/buy/' + slug;
    const signinPath = '/signin?next=' + encodeURIComponent(selfPath);

    const [offer, setOffer] = useState(null);
    const [phase, setPhase] = useState('loading');   // loading | ready | notFound | error
    const [sub, setSub] = useState(null);
    const [paying, setPaying] = useState(false);
    const [payErr, setPayErr] = useState(null);
    const [consentOpen, setConsentOpen] = useState(false);

    // Регистрация новичка прямо на странице.
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [agree, setAgree] = useState(false);
    const [errors, setErrors] = useState({});
    const [formErr, setFormErr] = useState(null);
    const [emailTaken, setEmailTaken] = useState(false);
    const [sending, setSending] = useState(false);
    const [sentTo, setSentTo] = useState(null);
    const [resent, setResent] = useState(false);

    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

    const personal = (workspaces || []).find((w) => w.kind === 'personal') || null;
    const personalId = personal ? personal.id : null;
    const uid = user ? user.id : null;

    const load = useCallback(async () => {
      setPhase('loading');
      const r = await getOffer(slug).catch(() => ({ ok: false, status: 0, data: null }));
      if (!alive.current) return;
      if (r.ok && r.data) {
        setOffer({ ...r.data, price_rub: Number(r.data.price_rub) });
        setPhase('ready');
        return;
      }
      setPhase(r.status === 404 ? 'notFound' : 'error');
    }, [slug, uid]);   // вошёл — сервер считает сумму под его личное пространство (quote)
    useEffect(() => { load(); }, [load]);

    // Подписка личного пространства — чтобы честно сказать, когда начнётся акция,
    // и не предлагать купить уже купленное.
    useEffect(() => {
      if (!user || !personalId) { setSub(null); return; }
      let on = true;
      subscriptionReal.getSubscription(personalId).then((s) => { if (on) setSub(s); }).catch(() => {});
      return () => { on = false; };
    }, [user, personalId]);

    const currentPrice = currentPriceOf(sub && sub.plan_details, sub);
    // Сумму и «встанет ли в очередь» решает сервер (`quote` — то же правило, что у
    // чекаута): у акции это полная цена, а при идущем платном тарифе (и Start) — ещё и
    // дата старта в очереди. Доплата с зачётом остатка — только у обычного тарифа дороже.
    const quote = offer && offer.quote && !offer.quote.error ? offer.quote : null;
    const quoteErr = offer && offer.quote && offer.quote.error ? offer.quote.error : null;
    const payAmount = quote ? Number(quote.amount_rub) : (offer ? offer.price_rub : 0);
    const prorated = isProratedQuote(quote, offer);
    const startsAt = quote
      ? (quote.queued ? (quote.queued_starts_at || (sub && sub.expires_at) || null) : null)
      : (offer ? queuedStartsAt(offer, sub, currentPrice) : null);
    const alreadyActive = !!(sub && offer && sub.plan === offer.slug && sub.is_active);
    const alreadyQueued = !!(sub && offer && sub.pending_plan === offer.slug && sub.pending_plan_prepaid);

    const pay = async (consent = false) => {
      if (paying || !offer || !personalId) return;
      if (!consent && consentNeededForPlan(offer)) { setConsentOpen(true); return; }
      setPaying(true); setPayErr(null);
      try {
        const r = await subscriptionReal.startCheckout(offer.slug, personalId, undefined, consent);
        // Мок-шлюз (DEBUG) — внутренний экран; Робокасса — настоящий переход.
        // paying не снимаем: страница живёт ещё мгновение, второй клик = второй платёж.
        if (r && r.invoice_id && r.redirect_url && /\/checkout\/mock\//.test(r.redirect_url)) {
          navigate('/checkout/mock/' + encodeURIComponent(r.invoice_id) + '?ws=' + encodeURIComponent(personalId));
          return;
        }
        if (r && r.redirect_url) { window.location.assign(r.redirect_url); return; }
        throw new Error('no redirect');
      } catch (e) {
        if (!alive.current) return;
        if (!consent && isConsentRequiredError(e)) { setPaying(false); setConsentOpen(true); return; }
        setConsentOpen(false);
        setPayErr((e && e.detail) || 'Не удалось начать оплату. Попробуйте ещё раз.');
        setPaying(false);
      }
    };

    const register = async (e) => {
      e.preventDefault();
      const errs = {};
      if (!name.trim()) errs.name = 'Укажите имя.';
      if (!EMAIL_RE.test(email.trim())) errs.email = 'Введите корректный email.';
      if (password.length < 8) errs.password = 'Минимум 8 символов.';
      setErrors(errs); setFormErr(null); setEmailTaken(false);
      if (Object.keys(errs).length || !agree) return;
      setSending(true);
      try {
        await authReal.register({ name, email: email.trim(), password, consent: agree, next: selfPath });
        if (alive.current) setSentTo(email.trim());
      } catch (err) {
        if (!alive.current) return;
        if (err && err.code === 'email_exists') {
          // Аккаунт есть — не тупик: вход с возвратом прямо на эту оплату.
          setEmailTaken(true);
          setFormErr('Аккаунт с этой почтой уже есть. Войдите — и сразу откроется оплата.');
          return;
        }
        // Ошибка валидации DRF приходит по полю (тот же разбор, что в SignUp).
        const fieldFor = { email: 'email', password: 'password', full_name: 'name', name: 'name' };
        const f = err && err.field && fieldFor[err.field];
        if (f) setErrors({ [f]: err.message });
        else setFormErr((err && err.message) || 'Не удалось создать аккаунт.');
      } finally {
        if (alive.current) setSending(false);
      }
    };

    const resend = async () => {
      try { await authReal.resendVerify(sentTo, selfPath); } catch { /* лимит — молча, кнопка честно сказала «отправили» */ }
      if (alive.current) setResent(true);
    };

    // ── Терминальные состояния ──
    const stateScreen = (icon, tone, title, text, action) => (
      <CheckoutLayout>
        <div className="paylink">
          <header className="checkout-head checkout-head--center">
            <div className={'checkout-icon checkout-icon--' + tone} aria-hidden="true"><Icon name={icon} size={28} /></div>
            <h1 className="t-h2">{title}</h1>
          </header>
          <p className="paylink__lead">{text}</p>
          <div className="checkout-actions">{action}</div>
        </div>
      </CheckoutLayout>
    );
    const homeButton = (
      <Button variant="secondary" size="lg" className="full" onClick={() => navigate(user ? '/chat' : '/signin')}>
        {user ? 'Перейти в Ресториум' : 'Перейти ко входу'}
      </Button>
    );

    if (phase === 'loading') {
      return (
        <CheckoutLayout>
          <div className="checkout-state" aria-busy="true">
            <Icon name="spinner" size={24} /><span className="text-dim">Загружаем предложение…</span>
          </div>
        </CheckoutLayout>
      );
    }
    if (phase === 'notFound') {
      return stateScreen('x-circle', 'bad', 'Предложение не найдено',
        'Ссылка не найдена или больше не действует.', homeButton);
    }
    if (phase === 'error') {
      return stateScreen('alert-triangle', 'warn', 'Не удалось открыть предложение',
        'Проверьте связь и попробуйте ещё раз.',
        <Button variant="primary" size="lg" className="full" iconLeft="refresh" onClick={load}>Попробовать снова</Button>);
    }
    if (!offer.on_sale) {
      const notYet = offer.sale_starts_at && new Date(offer.sale_starts_at) > new Date();
      return stateScreen('clock', 'warn',
        notYet ? 'Продажи ещё не начались' : 'Акция завершена',
        notYet
          ? `«${offer.name}» можно будет купить с ${fmt.date(offer.sale_starts_at, { timeZone: 'Europe/Moscow' })}.`
          : `Продажи «${offer.name}» закрыты.`,
        homeButton);
    }

    const saleDay = lastSaleDay(offer.sale_ends_at);
    const summary = (
      <div className="checkout-details">
        <CheckoutRow label="Тариф" value={offer.name} />
        <CheckoutRow label="Срок" value={periodText(offer.period_days)} />
        {offer.chat_messages_per_30d > 0 && (
          <CheckoutRow label="Запросы к ассистенту" value={offer.chat_messages_per_30d + ' в месяц'} />
        )}
        {offer.voice_seconds_per_30d > 0 && (
          <CheckoutRow label="Голосовой ввод" value={fmtSeconds(offer.voice_seconds_per_30d) + ' в месяц'} />
        )}
        <CheckoutRow label="Автопродление" value={offer.renewable ? 'Включено' : 'Нет, разовый платёж'} />
        <div className="paylink__total"><CheckoutRow label="К оплате" value={fmt.money(user ? payAmount : offer.price_rub)} /></div>
        {user && prorated && (
          <p className="t-caption text-dim">С зачётом остатка вашего текущего тарифа (цена тарифа — {fmt.money(offer.price_rub)}).</p>
        )}
      </div>
    );
    const head = (
      <header className="checkout-head checkout-head--center">
        <div className="checkout-icon checkout-icon--accent" aria-hidden="true"><Icon name="card" size={28} /></div>
        <div className="t-eyebrow text-dim">{offer.starts_after_current || offer.sale_ends_at ? 'Акция' : 'Тариф'}</div>
        <h1 className="t-h2">{offer.name}</h1>
        {saleDay && <p className="t-caption text-dim">Акция до {fmt.date(saleDay, { timeZone: 'Europe/Moscow' })} включительно</p>}
      </header>
    );

    // ── Вошёл: одна кнопка оплаты ──
    if (user) {
      if (quoteErr && !alreadyActive && !alreadyQueued && quoteErr.code !== 'same_plan') {
        // Купить отсюда нельзя — говорим почему и куда идти, без технических слов.
        const cheaper = quoteErr.code === 'downgrade_no_payment';
        const afterEnds = quoteErr.code === 'after_current_ends';
        return stateScreen('info', 'warn',
          cheaper ? 'Этот тариф дешевле вашего' : afterEnds ? 'Можно после текущего тарифа' : 'Сейчас купить нельзя',
          cheaper
            ? `«${offer.name}» дешевле вашего текущего тарифа — перейти на него можно в настройках тарифа, без оплаты.`
            : afterEnds
              ? `Ваш текущий тариф не продлевается — «${offer.name}» можно будет оформить, когда он закончится.`
              : quoteErr.detail,
          <Button variant="primary" size="lg" className="full" onClick={() => navigate('/settings?tab=subscription')}>
            Открыть мой тариф
          </Button>);
      }
      if (alreadyActive || alreadyQueued || (quoteErr && quoteErr.code === 'same_plan')) {
        // same_plan от сервера мог прийти раньше подписки — дата тогда не пишется.
        const active = !alreadyQueued;
        const until = sub && sub.expires_at ? fmt.date(sub.expires_at) : null;
        return stateScreen('check-circle', 'ok',
          active ? `«${offer.name}» уже действует` : `«${offer.name}» уже оплачен`,
          active
            ? (until ? `Тариф действует до ${until}.` : 'Тариф уже подключён.')
            : `Тариф начнёт действовать ${until || 'после текущего'}, когда закончится текущий.`,
          homeButton);
      }
      return (
        <CheckoutLayout>
          <div className="paylink">
            {head}
            {summary}
            {startsAt && <QueuedCallout sub={sub} offer={offer} startsAt={startsAt} />}
            {payErr && (
              <div className="callout callout--bad" role="alert"><Icon name="alert-circle" size={16} /><span>{payErr}</span></div>
            )}
            <div className="checkout-actions">
              <Button variant="primary" size="lg" className="full" loading={paying} disabled={paying || !personalId}
                onClick={() => phoneGate.run(() => pay())}>
                {`Оплатить ${fmt.money(payAmount)}`}
              </Button>
            </div>
            {/* Дата старта — подписью, а не в кнопке: «Оплатить 4 900 ₽ — начнётся
                26 окт 2026» не влезал в кнопку на 380 px (`.btn` — nowrap), края
                текста уходили белым по белому (QA 16.10.2026). */}
            {startsAt && <p className="t-caption text-dim">Начнётся {fmt.date(startsAt)}, когда закончится текущий тариф.</p>}
          </div>
          {phoneGate.modal}
          <RecurringConsentModal
            open={consentOpen}
            planName={offer.name}
            priceRub={offer.price_rub}
            periodDays={offer.period_days}
            prorated={false}
            busy={paying}
            onClose={() => setConsentOpen(false)}
            onConfirm={() => pay(true)} />
        </CheckoutLayout>
      );
    }

    // ── Не вошёл, письмо отправлено ──
    if (sentTo) {
      return stateScreen('mail', 'accent', 'Проверьте почту',
        <>Мы отправили письмо на <b>{sentTo}</b>. Нажмите в нём «Подтвердить email» — и сразу попадёте на оплату «{offer.name}». Откройте письмо на этом же устройстве: на другом попросим войти.</>,
        <>
          <Button variant="secondary" size="lg" className="full" disabled={resent} onClick={resend}>
            {resent ? 'Письмо отправлено ещё раз' : 'Отправить письмо ещё раз'}
          </Button>
          <a className="link checkout-link" href={selfPath}
            onClick={(e) => { e.preventDefault(); setSentTo(null); setResent(false); }}>Указать другую почту</a>
        </>);
    }

    // ── Не вошёл: короткая регистрация ──
    return (
      <CheckoutLayout>
        <div className="paylink">
          {head}
          {summary}
          <form className="stack stack-12" onSubmit={register} noValidate>
            <p className="t-body-sm text-mute">
              Создайте аккаунт — после подтверждения почты сразу откроется оплата.{' '}
              Уже есть аккаунт? <Link to={signinPath} className="link">Войти</Link>
            </p>
            {formErr && (
              <div className="callout callout--bad" role="alert">
                <Icon name="alert-circle" size={16} />
                <div className="stack stack-6">
                  <span>{formErr}</span>
                  {emailTaken && <Link to={signinPath} className="link">Войти и оплатить</Link>}
                </div>
              </div>
            )}
            <Field label="Имя" htmlFor="buy-name" error={errors.name}>
              <TextInput id="buy-name" value={name} autoComplete="name" invalid={errors.name ? true : undefined}
                placeholder="Как к вам обращаться" onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="E-mail" htmlFor="buy-email" error={errors.email}>
              <TextInput id="buy-email" type="email" value={email} autoComplete="email" invalid={errors.email ? true : undefined}
                placeholder="you@restaurant.ru" onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label="Пароль" htmlFor="buy-pass" error={errors.password}
              hint={!errors.password ? 'Минимум 8 символов' : undefined}>
              <PasswordInput id="buy-pass" value={password} autoComplete="new-password" invalid={errors.password ? true : undefined}
                placeholder="Придумайте пароль" onChange={(e) => setPassword(e.target.value)} />
            </Field>
            <div className="auth-form__agree">
              <input id="buy-agree" type="checkbox" className="checkbox__input" checked={agree}
                aria-label="Я принимаю оферту и политику конфиденциальности"
                onChange={(e) => setAgree(e.target.checked)} />
              <label htmlFor="buy-agree" className="checkbox__box" aria-hidden="true">
                <Icon name="check" size={12} />
              </label>
              <span className="auth-form__agree-text">
                <label htmlFor="buy-agree" className="auth-form__agree-lead">Я принимаю</label>{' '}
                <Link to="/legal/offer" className="link">оферту</Link> и{' '}
                <Link to="/legal/privacy" className="link">политику конфиденциальности</Link>
              </span>
            </div>
            <Button type="submit" variant="primary" size="lg" className="full" loading={sending} disabled={!agree}>
              Продолжить к оплате
            </Button>
          </form>
        </div>
      </CheckoutLayout>
    );
  }

export { BuyPlan, QueuedCallout, isProratedQuote };
